import React, { useEffect, useMemo, useState } from 'react';
import { ChildProfile } from '../models/childProfile';
import ChildAvatar from './ChildAvatar';

interface Props {
  staffName: string;
  childProfiles: ChildProfile[];
  /** Whitelist as it stands in the editor. Edits here are local until Done. */
  value: number[];
  /** External users see only whitelisted children, so an empty list means they
   *  see nothing — worth saying out loud rather than leaving it to be found. */
  isExternal: boolean;
  onApply: (childIds: number[]) => void;
  onCancel: () => void;
}

const AVATAR = 32;

const byName = (a: ChildProfile, b: ChildProfile) =>
  `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);

/**
 * Full whitelist editor. Building a whitelist is a bulk, exploratory job —
 * search, scan, add several, review — which never fitted the 560px staff panel.
 * Here the two halves sit side by side so "who can they see?" is answerable at
 * a glance instead of by scrolling past everyone who isn't selected.
 */
const ChildWhitelistModal: React.FC<Props> = ({
  staffName,
  childProfiles,
  value,
  isExternal,
  onApply,
  onCancel,
}) => {
  const [picked, setPicked] = useState<number[]>(value);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const pickedSet = useMemo(() => new Set(picked), [picked]);

  // Search filters the available side only — the selected side is the answer to
  // "who can they see?", and hiding rows from it would misreport that.
  const available = useMemo(() => {
    const q = query.trim().toLowerCase();
    return childProfiles
      .filter(c => !pickedSet.has(c.id))
      .filter(c => !q || [c.first_name, c.last_name, c.initials, c.alias || ''].join(' ').toLowerCase().includes(q))
      .sort(byName);
  }, [childProfiles, pickedSet, query]);

  const selected = useMemo(
    () => childProfiles.filter(c => pickedSet.has(c.id)).sort(byName),
    [childProfiles, pickedSet],
  );

  const add = (id: number) => setPicked(p => [...p, id]);
  const remove = (id: number) => setPicked(p => p.filter(x => x !== id));
  const addAllVisible = () => setPicked(p => Array.from(new Set([...p, ...available.map(c => c.id)])));

  const row = (c: ChildProfile, isPicked: boolean) => (
    <button
      key={c.id}
      type="button"
      className={`cwl-row${isPicked ? ' is-picked' : ''}`}
      onClick={() => (isPicked ? remove(c.id) : add(c.id))}
      title={isPicked ? `Remove ${c.first_name} ${c.last_name}` : `Add ${c.first_name} ${c.last_name}`}
    >
      <ChildAvatar
        child={c}
        size={AVATAR}
        imageUrl={c.profile_image_thumbnail_url || c.profile_image_url || null}
        showBadge={false}
      />
      <span className="cwl-row__body">
        <span className="cwl-row__name">{c.first_name} {c.last_name}</span>
        {c.alias && <span className="cwl-row__alias">"{c.alias}"</span>}
      </span>
      <i className={`fas ${isPicked ? 'fa-xmark' : 'fa-plus'} cwl-row__action`} aria-hidden="true" />
    </button>
  );

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered cwl-dialog">
        <div className="modal-content ivory-panel">
          <div className="modal-header">
            <div>
              <h5 className="modal-title">Choose children</h5>
              <div className="cwl-sub">
                {staffName ? `${staffName} will see the children you pick here.` : 'Pick the children this person can see.'}
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onCancel} aria-label="Close" />
          </div>

          <div className="modal-body cwl-body">
            <div className="cwl-panes">
              {/* Available */}
              <section className="cwl-pane" aria-label="Available children">
                <header className="cwl-pane__head">
                  <span className="cwl-pane__title">
                    Available <span className="cwl-count">{available.length}</span>
                  </span>
                  <button
                    type="button"
                    className="cwl-linkbtn"
                    onClick={addAllVisible}
                    disabled={available.length === 0}
                  >
                    Add all{query.trim() ? ' matches' : ''}
                  </button>
                </header>

                <div className="cwl-search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                    type="search"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search children"
                    aria-label="Search children"
                  />
                </div>

                <div className="cwl-list">
                  {available.length === 0 ? (
                    <p className="cwl-empty">
                      {query.trim()
                        ? <>No children match <strong>"{query.trim()}"</strong>.</>
                        : childProfiles.length === 0
                          ? 'No children in this organisation yet.'
                          : 'Every child is already on the list.'}
                    </p>
                  ) : (
                    available.map(c => row(c, false))
                  )}
                </div>
              </section>

              {/* Selected */}
              <section className="cwl-pane cwl-pane--selected" aria-label="Selected children">
                <header className="cwl-pane__head">
                  <span className="cwl-pane__title">
                    On the list <span className="cwl-count cwl-count--on">{selected.length}</span>
                  </span>
                  <button
                    type="button"
                    className="cwl-linkbtn"
                    onClick={() => setPicked([])}
                    disabled={selected.length === 0}
                  >
                    Remove all
                  </button>
                </header>

                <div className="cwl-list">
                  {selected.length === 0 ? (
                    <p className="cwl-empty">
                      {isExternal
                        ? 'Nobody yet — an external user with an empty list sees no children at all.'
                        : 'Nobody yet — an empty list means access to every child.'}
                    </p>
                  ) : (
                    selected.map(c => row(c, true))
                  )}
                </div>
              </section>
            </div>
          </div>

          <div className="modal-footer">
            <span className="cwl-footnote">
              {selected.length === 0
                ? (isExternal ? 'Sees no children' : 'Access to all children')
                : `Sees ${selected.length} ${selected.length === 1 ? 'child' : 'children'}`}
            </span>
            <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => onApply(picked)}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildWhitelistModal;
